import { ClickAwayListener } from '@mui/material';
import clsx from 'clsx';
import { LoadingSpinner, Stack } from 'components';
import { TextAreaInputField } from 'components/inputField/textAreaInpuField';
import { setIn, useFormik } from 'formik';
import debounce from 'lodash.debounce';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { LastSaved } from 'modules/notes/notePopup/noteView/addendumForm/lastSaved';
import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { postUpdateSnapshot } from 'store/clients/clientsActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';

import style from './snapshot.module.scss';

const Snapshot = () => {
  const [edit, showEdit] = useState(false);
  const client = useAppSelector((state) => state.clients.client.data);
  const { clientId } = useParams();
  const { organizationId } = getLsUserRoleObject();
  const dispatch = useAppDispatch();
  const { enableFileUpload } = useFlags();
  const [showSection, setShowSection] = useState(true);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const formik = useFormik({
    initialValues: {
      snapshot: clientId ? client?.snapshot ?? '' : ''
    },
    onSubmit: () => {}
  });

  const openEdit = () => {
    showEdit(true);
    setShowSection(true);
  };

  const handleToggleSection = () => {
    setShowSection(!showSection);
    if (showSection) {
      showEdit(false);
    }
  };

  const autosaveForm = async (values: { snapshot: string }) => {
    if (!clientId) return;
    setIsAutosaving(true);
    const response = await dispatch(
      postUpdateSnapshot({ snapshot: values.snapshot, clientId, organizationId })
    );

    setIsAutosaving(false);
    if (response.meta.requestStatus === 'fulfilled') {
      setLastSaved(new Date().toISOString());
    }
  };

  const debounceFn = useCallback(debounce(autosaveForm, 250), []);

  const handleSnapshotChange = async (event: ChangeEvent<HTMLTextAreaElement>) => {
    const clippedSnapshot = event.target.value.slice(0, 500);

    const values = setIn(formik.values, event.target.name, clippedSnapshot);

    formik.handleChange({
      ...event,
      target: { ...event.target, value: clippedSnapshot, name: event.target.name }
    });

    // we call the autosave function here to avoid the mess with useEffect
    // so we only trigger autosave when snapshot changes
    // if we were to do this in useEffect, it would trigger autosave on initial render as well
    debounceFn(values);
  };

  return (
    <Stack width="100%" data-test-id="profile-snapshot">
      <div className={style.snapshotWrapper}>
        <p className={style.snapshot}>Snapshot</p>
        <div>
          <span
            onClick={openEdit}
            className={clsx('material-icons', style.icon)}
            style={{
              color: edit ? '#8da0b9' : ''
            }}
            data-disabled={edit}
            data-test-id="edit-icon"
          >
            edit
          </span>

          {enableFileUpload && (
            <span
              onClick={handleToggleSection}
              className={clsx('material-icons-outlined', style.icon)}
            >
              {showSection ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            </span>
          )}
        </div>
      </div>
      {enableFileUpload && !showSection ? null : (
        <>
          {edit && (
            <form onSubmit={formik.handleSubmit}>
              <Stack width="100%" mt="4px">
                <Stack maxHeight="212px">
                  <ClickAwayListener onClickAway={() => showEdit(false)}>
                    <TextAreaInputField
                      ref={textareaRef}
                      onBlur={formik.handleBlur}
                      onChange={handleSnapshotChange}
                      name="snapshot"
                      value={formik.values.snapshot}
                      enableCounter
                      maxLength={500}
                      placeholder="Type here"
                      autoFocus
                    />
                  </ClickAwayListener>
                  <Stack display="flex" flexDirection="row" width="100%" height="24px" mt="8px">
                    {isAutosaving ? (
                      <LoadingSpinner size={24} />
                    ) : (
                      <LastSaved lastSaved={lastSaved} />
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </form>
          )}
          {!edit &&
            (formik.values?.snapshot == null || formik.values?.snapshot === '' ? (
              <Stack width="100%" mt="4px">
                <Stack maxHeight="212px">
                  <TextAreaInputField
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      showEdit(false);
                    }}
                    onChange={handleSnapshotChange}
                    name="snapshot"
                    value={formik.values.snapshot}
                    enableCounter
                    maxLength={500}
                    placeholder="Type here"
                    disabled
                  />
                  <Stack
                    display="flex"
                    flexDirection="row"
                    width="100%"
                    height="24px"
                    mt="8px"
                  ></Stack>
                </Stack>
              </Stack>
            ) : (
              <Stack maxHeight="156px" overflow="auto" padding="16px 14px">
                <p className={style.snapshotText}>{formik.values.snapshot}</p>
              </Stack>
            ))}
        </>
      )}
    </Stack>
  );
};

export default Snapshot;
